import PropTypes from 'prop-types';
import { Inline, vars } from '@etg/wings';
import { css, etiColors, mediaQueries } from '@eti/styles';
import { Icon, PartnerLogo } from '@eti/components';
import { cancelForAnyReason, flexibleTicket, ProductIcon } from '@eti/products';
import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { CurrencyAmount, TripCampaign, TripCharacteristics } from '@eti/types';
import Media from 'react-media';
import { change } from 'redux-form';
import { connect } from 'react-redux';
import { bounds as boundsPropTypes } from '../../../../widgets/trip-details/propTypes';
import { useToggle } from '../../../../common/custom-hooks/toggle';
import { tripIncludesTrainSegments } from '../../../../widgets/trip-details/utils/format';
import DebugProviderSystemBar from '../../../../widgets/trip-details/components/DebugProviderSystemBar';
import { ALL, SOME } from '../../../../constants/transportationMeansConstants';
import { getMarketingCarrierNamesToDisplayInTaxBreakdown } from '../../../../utils/carriers';
import { shouldShowDebugProviderSystemBar } from '../../../../utils/debug';
import {
  getDiscountedPaymentMethod,
  getMinNumberOfSeats,
  shouldShowSeatsLeftNotification,
} from '../../utils/resultUtils';
import { RESULT } from '../../../../constants/formNamesConstants';
import { AVAILABLE_BUNDLE } from '../../constants/resultConstants';
import { useGetAncillaryBundle } from '../../utils/ancillaryBundleUtils';
import { transformBundleData } from '../../../../utils/transformBundleData';
import { hasRyanAirTicketingOrMarketingCarrier } from '../../../../utils/carriersUtils';
import ResultBookButton from './ResultBookButton';
import ResultTripDetails from './ResultTripDetails';

const resultTripContainerStyles = (extraSpaceAbove, isExpanded) => css`
  ${extraSpaceAbove === true && `margin-top: 10px;`}
  box-shadow: ${isExpanded ? '0 0 25px rgba(0, 0, 0, 0.2)' : '0 0 8px rgba(0, 0, 0, 0.12)'};

  & > div:first-child {
    border-top-left-radius: 3px;
    border-top-right-radius: 3px;
  }

  & > div:last-child {
    border-bottom-left-radius: 3px;
    border-bottom-right-radius: 3px;
  }
`;

const bookButtonsWrapperStyles = (
  hasDiscountedPrice,
  shouldShowSeatsLeftNotificationMessage,
) => css`
  background-color: #fcfcfc;
  border-radius: ${hasDiscountedPrice ? 0 : '0 0 0.1875rem 0.1875rem'};
  border-top: 0;
  display: flex;
  flex-direction: column;
  padding-top: ${shouldShowSeatsLeftNotificationMessage ? '10px' : '0'};
  width: 100%;

  @media ${mediaQueries.medium.up} {
    flex-direction: row;
  }

  @media ${mediaQueries.large.up} {
    padding-top: 0;
  }
`;

const resultTripHeaderStyles = css`
  background-color: ${vars.colors.card.highlight.background};
  color: ${vars.colors.card.highlight.foreground};
  font-size: 0.875rem;
  font-weight: 700;
  height: 30px;
  line-height: 30px;
  padding: 0 10px;
`;

const iconStyles = css`
  height: 24px;
  width: 24px;

  @media ${mediaQueries.large.up} {
    height: 32px;
    width: 32px;
  }
`;

const campaignImageStyle = css`
  display: block;
  width: 100%;
`;

const standardIconsContainerStyles = css`
  display: flex;
  flex-direction: row;
`;

const standardTicketIconStyles = (isConfirmation) => css`
  background-color: ${isConfirmation ? etiColors.confirmation.solid : etiColors.grayScale[6]};
  border-radius: 50%;
  fill: #fff;
  ${iconStyles};
  padding: 4px;

  &:nth-child(2) {
    margin-left: 10px;

    [dir='rtl'] & {
      margin-left: unset;
      margin-right: 10px;
    }
  }
`;

const flexibleTicketIconStyles = (isConfirmation) => css`
  ${iconStyles};

  div:first-child {
    svg {
      path {
        fill: ${isConfirmation ? etiColors.confirmation.solid : etiColors.grayScale[6]};
      }
    }
  }
`;

const cFARTicketIconStyles = css`
  height: 24px;
  width: 24px;

  @media ${mediaQueries.large.up} {
    height: 38px;
    width: 38px;
  }
`;

const validWithVoucherStyles = css`
  align-items: center;
  background-color: ${etiColors.confirmation.solid};
  border-radius: 2px;
  color: #fff;
  display: inline-flex;
  font-size: 0.75rem;
  font-weight: 700;
  height: 1.5em;
  line-height: 1.5em;
  padding: 0 0.5em;
  position: absolute;
  right: 3em;
  top: -0.75em;
  z-index: 1;

  html[dir='rtl'] & {
    left: 3em;
    right: auto;
  }
`;

const voucherIconStyle = css`
  height: 0.625rem;
  margin: 0 5px 0 0;
  width: 0.625rem;

  html[dir='rtl'] & {
    margin: 0 0 0 5px;
  }
`;

const priceInfoContainerStyles = css`
  align-items: center;
  background-color: ${etiColors.white};
  border-top: 1px solid rgb(204 204 204 / 30%);
  display: flex;
  justify-content: flex-end;
  padding: 5px 10px;
  text-align: center;

  @media (min-width: 480px) {
    padding: 5px 20px;
  }
`;

const priceInfoTextStyles = css`
  color: #fcfcfc;
  font-size: 0.625rem;
  line-height: 14px;
  margin-left: 5px;

  html[dir='rtl'] & {
    margin-left: 0;
    margin-right: 5px;
  }

  @media (min-width: 480px) {
    font-size: 0.6875rem;
    line-height: 15px;
  }
`;

const priceInfoIconBackgroundStyles = css`
  align-items: center;
  background-color: ${etiColors.warning.solid};
  border-radius: 100px;
  display: flex;
  height: 10px;
  justify-content: center;
  width: 10px;

  @media (min-width: 480px) {
    height: 12px;
    width: 12px;
  }
`;

const priceInfoIconStyles = css`
  fill: ${etiColors.white};
  height: 8px;
  width: 8px;
`;

const partnerLogoStyles = css`
  filter: drop-shadow(0 -3px 5px rgb(0 0 0 / 10%));
  height: 26px;
  position: absolute;
  right: 2rem;
  top: -13px;
  z-index: 1;

  @media ${mediaQueries.large.up} {
    height: 34px;
    right: 3rem;
    top: -17px;
  }

  html[dir='rtl'] & {
    left: 2rem;
    right: unset;

    @media ${mediaQueries.large.up} {
      left: 3rem;
      right: unset;
    }
  }
`;

const StandardTicketIcons = ({ bounds, isBookButtonGreenEnabled }) => {
  if (tripIncludesTrainSegments(bounds) === SOME) {
    return (
      <div
        className={standardIconsContainerStyles}
        data-testid="result-bookButton-planeTrainStandardIcon"
      >
        <Icon className={standardTicketIconStyles(isBookButtonGreenEnabled)} id="plane-bold" />
        <Icon className={standardTicketIconStyles(isBookButtonGreenEnabled)} id="train" />
      </div>
    );
  }

  if (tripIncludesTrainSegments(bounds) === ALL) {
    return (
      <div
        className={standardIconsContainerStyles}
        data-testid="result-bookButton-trainStandardIcon"
      >
        <Icon className={standardTicketIconStyles(isBookButtonGreenEnabled)} id="train" />
      </div>
    );
  }

  return (
    <div className={standardIconsContainerStyles} data-testid="result-bookButton-planeStandardIcon">
      <Icon className={standardTicketIconStyles(isBookButtonGreenEnabled)} id="plane-bold" />
    </div>
  );
};

const inlineStyles = css`
  background-color: #fcfcfc;
  color: ${vars.colors.promotional.foreground};
  font-size: 0.875rem;
  padding: 8px 10px;

  @media ${mediaQueries.medium.up} {
    padding: 4px 10px 8px 20px;
  }

  @media ${mediaQueries.large.up} {
    padding: 4px 20px 12px;
  }
`;

const ResultTrip = ({
  addTrip,
  bounds,
  cfarTicketAvailability,
  discountAmount,
  flexibleTicketAvailability,
  flightId,
  hasBookingButton,
  isCheckedBaggageIncluded,
  isMultiStop,
  isSelectTripLoading,
  isSelectedTripRefetching,
  isVI,
  language,
  numberOfTravelers,
  onShowAncillaryBundleModal = () => {},
  paymentMethodPrices,
  redirectToDBF,
  redirectToTravelerDetails,
  selectionKey,
  setBundleData,
  shouldDisplayBrandedFares,
  shouldShowPartnerLogo,
  title,
  travelerPriceBreakdown,
  travelerPrices,
  tripCampaign,
  tripCharacteristics,
  tripProviderSystems,
  validWithVoucher,
}) => {
  const { partner } = useSiteContext();
  const { t } = useTranslation();
  const { p } = useProperty();
  const { isOpen, handleToggle } = useToggle();
  const isDBFEnabled = p('TravelerDetails.DynamicFlow.Enabled');
  const isBookButtonGreenEnabled = p('Result.BookBtn.Green.Enable');
  const isBookFlexibleButtonGreenEnabled = p('Result.BookFlexBtn.Green.Enable');
  const seatsThreshold = p('Air.Result.ShowNumberOfSeatsLeft.WhenLessThan');
  const numberOfSeatsLeft = getMinNumberOfSeats(bounds);
  const shouldShowSeatsLeftNotificationMessage = shouldShowSeatsLeftNotification(
    numberOfSeatsLeft,
    numberOfTravelers,
    seatsThreshold,
  );

  const zeroTaxesCarriers = p('BookingFlow.HideTaxIfZero.Carriers');
  const carriersToDisplayInTaxBreakdown = p('BookingFlow.SupplierCostMarkupBreakdown.Carriers');
  const marketingCarrierNames = getMarketingCarrierNamesToDisplayInTaxBreakdown(
    bounds,
    carriersToDisplayInTaxBreakdown,
    zeroTaxesCarriers,
  );

  const isPaymentMethodInformationEnabled = p('Result.PaymentMethod.DiscountInformation.Enable');
  const priceType = p('IbeClient.Result.ShowPrice.Type');
  const { name: paymentMethodName, hasDiscountedPrice } =
    getDiscountedPaymentMethod(paymentMethodPrices);
  const priceInfoTexts = {
    PER_ADULT: t('Result.PaymentMethod.DiscountInformation.PerAdult.Text', paymentMethodName),
    PER_TRAVELER: t('Result.PaymentMethod.DiscountInformation.PerTraveler.Text', paymentMethodName),
    TOTAL: t('Result.PaymentMethod.DiscountInformation.Total.Text', paymentMethodName),
  };
  const priceInfoText = priceInfoTexts[priceType];

  const getAncillaryBundle = useGetAncillaryBundle(selectionKey);

  const getAncillaryBundles = async (isFlexibleTicketAvailable) => {
    let ancillaryBundle = [];

    if (p('Result.Show.Overlay')) {
      const { data } = await getAncillaryBundle();
      ancillaryBundle = data?.ancillaryBundle ?? [];
    }

    if (ancillaryBundle.length > 0) {
      const ancillaryBundleData = transformBundleData(ancillaryBundle[0]);

      setBundleData({ ...ancillaryBundleData, selectionKey });
      onShowAncillaryBundleModal();
    } else if (!isFlexibleTicketAvailable && isDBFEnabled) {
      redirectToDBF();
    } else {
      redirectToTravelerDetails();
    }
  };

  const isBookingTimeEnabled = p('Result.BookingTime.Enabled');
  const isRyanAirTicketingOrMarketingCarrierPresent = hasRyanAirTicketingOrMarketingCarrier(
    p('IbeClient.RyanairMarketingCarriers'),
    bounds,
  );

  return (
    <>
      <div className={resultTripContainerStyles(validWithVoucher, isOpen)}>
        {title && (
          <div className={resultTripHeaderStyles} data-testid="result-tripHeader-title">
            {title}
          </div>
        )}
        {shouldShowDebugProviderSystemBar(tripProviderSystems) && (
          <DebugProviderSystemBar providerSystems={tripProviderSystems} />
        )}
        {tripCampaign && (
          <Media query={mediaQueries.medium.up}>
            {(matches) => (
              <img
                alt={t('Result.Campaigns.AltText')}
                className={campaignImageStyle}
                src={
                  matches
                    ? tripCampaign.campaignImageUrlDesktop
                    : tripCampaign.campaignImageUrlMobile
                }
              />
            )}
          </Media>
        )}
        {validWithVoucher && (
          <div className={validWithVoucherStyles} data-testid="valid-with-voucher-tag">
            <Icon className={voucherIconStyle} color={etiColors.white} id="tag" />
            <p>{t('Result.SSR.ValidVoucher.Text')}</p>
          </div>
        )}
        {shouldShowPartnerLogo && (
          <div data-testid="meta-partner-logo">
            <PartnerLogo className={partnerLogoStyles} collection="meta-partners" id={partner} />
          </div>
        )}
        <ResultTripDetails
          bounds={bounds}
          flightId={flightId}
          isBaggageIncluded={isCheckedBaggageIncluded}
          isExpanded={isOpen}
          isMultiStop={isMultiStop}
          isRyanAirTicketingOrMarketingCarrierPresent={isRyanAirTicketingOrMarketingCarrierPresent}
          isVI={isVI}
          language={language}
          numberOfSeatsLeft={numberOfSeatsLeft}
          onClick={handleToggle}
          shouldShowSeatsLeftNotificationMessage={shouldShowSeatsLeftNotificationMessage}
          tripCharacteristics={tripCharacteristics}
        />
        <div
          className={bookButtonsWrapperStyles(
            hasDiscountedPrice,
            shouldShowSeatsLeftNotificationMessage,
          )}
          data-testid="result-bookButtons-wrapper"
        >
          <ResultBookButton
            addTrip={addTrip}
            discountAmount={discountAmount}
            getAncillaryBundles={getAncillaryBundles}
            hasBookingButton={hasBookingButton}
            isDisabled={isSelectTripLoading}
            isPrimaryButton={
              (!flexibleTicketAvailability && !cfarTicketAvailability) || isBookButtonGreenEnabled
            }
            isRyanAirTicketingOrMarketingCarrierPresent={
              isRyanAirTicketingOrMarketingCarrierPresent
            }
            isSelectedTripRefetching={isSelectedTripRefetching}
            isVI={isVI}
            marketingCarrierNames={marketingCarrierNames}
            paymentMethodPrices={paymentMethodPrices}
            selectionKey={selectionKey}
            shouldDisplayBrandedFares={shouldDisplayBrandedFares}
            travelerPriceBreakdown={travelerPriceBreakdown}
            travelerPrices={travelerPrices}
            tripIcon={
              (flexibleTicketAvailability || cfarTicketAvailability) && (
                <StandardTicketIcons
                  bounds={bounds}
                  isBookButtonGreenEnabled={isBookButtonGreenEnabled}
                />
              )
            }
            tripTypeTitle={
              (flexibleTicketAvailability || cfarTicketAvailability) &&
              t('Air.Result.StandardTripType')
            }
          />
          {flexibleTicketAvailability && (
            <ResultBookButton
              addTrip={addTrip}
              discountAmount={discountAmount}
              flexibleTicketPrices={
                flexibleTicketAvailability?.sellSpecification?.sellPriceTravelers
              }
              getAncillaryBundles={() => getAncillaryBundles(true)}
              hasBookingButton={hasBookingButton}
              isDisabled={isSelectTripLoading}
              isPrimaryButton={isBookFlexibleButtonGreenEnabled}
              isSelectedTripRefetching={isSelectedTripRefetching}
              marketingCarrierNames={marketingCarrierNames}
              paymentMethodPrices={paymentMethodPrices}
              selectionKey={selectionKey}
              shouldDisplayBrandedFares={shouldDisplayBrandedFares}
              travelerPriceBreakdown={travelerPriceBreakdown}
              travelerPrices={travelerPrices}
              tripIcon={
                <ProductIcon
                  className={flexibleTicketIconStyles(isBookFlexibleButtonGreenEnabled)}
                  id={flexibleTicket.name}
                />
              }
              tripTypeTitle={t('flexibleTicketHeaderTitle')}
            />
          )}
          {cfarTicketAvailability && (
            <ResultBookButton
              addTrip={addTrip}
              cfarTicketPrices={cfarTicketAvailability?.sellSpecification?.price?.price?.value}
              discountAmount={discountAmount}
              getAncillaryBundles={getAncillaryBundles}
              hasBookingButton={hasBookingButton}
              isDisabled={isSelectTripLoading}
              isPrimaryButton={isBookFlexibleButtonGreenEnabled}
              isSelectedTripRefetching={isSelectedTripRefetching}
              marketingCarrierNames={marketingCarrierNames}
              paymentMethodPrices={paymentMethodPrices}
              selectionKey={selectionKey}
              shouldDisplayBrandedFares={shouldDisplayBrandedFares}
              travelerPriceBreakdown={travelerPriceBreakdown}
              travelerPrices={travelerPrices}
              tripIcon={
                <ProductIcon className={cFARTicketIconStyles} id={cancelForAnyReason.name} />
              }
              tripTypeTitle={t('cancelForAnyReasonTitle')}
            />
          )}
        </div>
        {isBookingTimeEnabled && (
          <Inline align="end" className={inlineStyles} data-testid="average-booking-time">
            {t('Result.BookingTime.Text')}
          </Inline>
        )}
        {isPaymentMethodInformationEnabled && hasDiscountedPrice && (
          <div className={priceInfoContainerStyles} data-testid="payment-method-discount-info">
            <div className={priceInfoIconBackgroundStyles}>
              <Icon className={priceInfoIconStyles} id="dollarsign" />
            </div>
            <span className={priceInfoTextStyles}>{priceInfoText}</span>
          </div>
        )}
      </div>
    </>
  );
};

StandardTicketIcons.propTypes = {
  bounds: boundsPropTypes,
  isBookButtonGreenEnabled: PropTypes.bool,
};

ResultTrip.propTypes = {
  addTrip: PropTypes.func,
  bounds: boundsPropTypes,
  cfarTicketAvailability: PropTypes.shape({
    sellSpecification: PropTypes.shape({
      price: PropTypes.shape({
        price: PropTypes.shape({
          value: PropTypes.number,
        }),
      }),
    }),
  }),
  discountAmount: PropTypes.number,
  flexibleTicketAvailability: PropTypes.shape({
    sellSpecification: PropTypes.shape({
      sellPriceTravelers: PropTypes.arrayOf(
        PropTypes.shape({
          price: PropTypes.shape({
            price: PropTypes.shape({
              value: PropTypes.number,
            }),
          }),
        }),
      ),
    }),
  }),
  flightId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hasBookingButton: PropTypes.bool,
  isCheckedBaggageIncluded: PropTypes.bool,
  isMultiStop: PropTypes.bool,
  isSelectedTripRefetching: PropTypes.bool,
  isSelectTripLoading: PropTypes.bool,
  isVI: PropTypes.bool,
  language: PropTypes.string,
  numberOfTravelers: PropTypes.number,
  onShowAncillaryBundleModal: PropTypes.func,
  paymentMethodPrices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.oneOfType([PropTypes.number, PropTypes.shape({ value: PropTypes.number })]),
    }),
  ),
  redirectToDBF: PropTypes.func,
  redirectToTravelerDetails: PropTypes.func,
  selectionKey: PropTypes.string,
  setBundleData: PropTypes.func,
  shouldDisplayBrandedFares: PropTypes.bool,
  shouldShowPartnerLogo: PropTypes.bool,
  title: PropTypes.string,
  travelerPriceBreakdown: PropTypes.arrayOf(
    PropTypes.shape({
      ageType: PropTypes.string,
      markup: PropTypes.number,
      price: PropTypes.number,
      quantity: PropTypes.number,
      taxesAndFees: PropTypes.arrayOf(
        PropTypes.shape({
          category: PropTypes.string,
          taxes: PropTypes.arrayOf(
            PropTypes.shape({
              amount: CurrencyAmount,
              code: PropTypes.string,
              title: PropTypes.string,
            }),
          ),
        }),
      ),
      totalPrice: PropTypes.number,
      totalPriceSum: PropTypes.number,
      vat: PropTypes.number,
    }),
  ),
  travelerPrices: PropTypes.arrayOf(
    PropTypes.shape({
      ageType: PropTypes.string,
      markup: PropTypes.number,
      price: PropTypes.number,
      quantity: PropTypes.number,
      totalPrice: PropTypes.number,
      totalPriceSum: PropTypes.number,
      vat: PropTypes.number,
    }),
  ),
  tripCampaign: TripCampaign,
  tripCharacteristics: PropTypes.arrayOf(TripCharacteristics),
  tripProviderSystems: PropTypes.arrayOf(PropTypes.string),
  // eslint-disable-next-line react/boolean-prop-naming
  validWithVoucher: PropTypes.bool,
};

const mapDispatchToProps = (dispatch) => ({
  setBundleData: (data) => {
    dispatch(change(RESULT, AVAILABLE_BUNDLE, data));
  },
});

export default connect(null, mapDispatchToProps)(ResultTrip);
